import React, { ReactNode } from "react";
import {
  Box,
  Sheet,
  Typography,
  Button,
  Avatar,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Divider,
} from "@mui/joy";
import {
  Search as SearchIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const navigationItems = [
    {
      path: "/search",
      label: "ค้นหาผู้สมัคร",
      icon: <SearchIcon />,
    },
  ];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <Sheet
        sx={{
          width: 280,
          p: 2,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Logo/Title */}
        <Box sx={{ mb: 3 }}>
          <Typography
            level="h3"
            sx={{ color: "primary.500", fontWeight: "bold" }}
          >
            MP101 Browser
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            ระบบค้นหาข้อมูลผู้สมัคร
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1 }}>
          {navigationItems.map((item) => (
            <Button
              key={item.path}
              variant={location.pathname === item.path ? "soft" : "plain"}
              color={location.pathname === item.path ? "primary" : "neutral"}
              startDecorator={item.icon}
              sx={{
                justifyContent: "flex-start",
                mb: 1,
                width: "100%",
              }}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* User Menu */}
        <Box sx={{ mt: "auto", pt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Dropdown>
            <MenuButton
              variant="plain"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                borderRadius: "sm",
                width: "100%",
                justifyContent: "flex-start",
              }}
            >
              <Avatar size="sm" src={user?.picture}></Avatar>
              <Box sx={{ textAlign: "left", minWidth: 0, flex: 1 }}>
                <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
                  {user?.name}
                </Typography>
                <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                  {user?.email}
                </Typography>
              </Box>
            </MenuButton>
            <Menu>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                ออกจากระบบ
              </MenuItem>
            </Menu>
          </Dropdown>
        </Box>
      </Sheet>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          p: 4,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
